import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Chip,
  Stack,
  Paper,
  Grid,
  Tabs,
  Tab,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FactoryIcon from "@mui/icons-material/Factory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import WarningIcon from "@mui/icons-material/Warning";
import dayjs from "dayjs";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: #f5f5f5;
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

function WorkOrderLog() {
  const { group_code, project_code } = useParams();

  const [loading, setLoading] = useState(false);
  const [allInstructions, setAllInstructions] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [filterTab, setFilterTab] = useState(1);
  const [inputValues, setInputValues] = useState({});
  const [finishCheckboxes, setFinishCheckboxes] = useState({});
  const [stockIncreaseCheckboxes, setStockIncreaseCheckboxes] = useState({});

  // ✅ Dialog 상태 추가
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    group: null,
    items: [],
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchAllInstructions();
  }, [group_code, project_code]);

  const fetchAllInstructions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${TPC_URL}/api/instruction/getAllInstructions?group_code=${group_code}&project_code=${project_code}`
      );

      if (response.data && Array.isArray(response.data)) {
        setAllInstructions(response.data);
        processInstructionData(response.data);
      }
    } catch (error) {
      console.error("Error fetching all instructions:", error);
      setSnackbar({
        open: true,
        message: "작업지시 데이터 로딩 실패",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const processInstructionData = (data) => {
    const grouped = {};

    data.forEach((item) => {
      const key = item.instruction_id;

      if (!grouped[key]) {
        grouped[key] = {
          instruction_id: item.instruction_id,
          instruction_code: item.instruction_code,
          register_date: item.register_date,
          instruction_date: item.instruction_date,
          is_all_finished: true,
          items: [],
        };
      }

      grouped[key].items.push(item);
    });

    Object.values(grouped).forEach((group) => {
      const itemMap = new Map();

      group.items.forEach((item) => {
        const uniqueKey = `${item.item_id}_${item.machine_id}_${item.shift_id}`;

        if (itemMap.has(uniqueKey)) {
          const existingItem = itemMap.get(uniqueKey);
          existingItem.result_qty += item.result_qty || 0;

          if (item.status === 1) {
            existingItem.is_finished = 1;
          }
        } else {
          itemMap.set(uniqueKey, { ...item });
        }
      });

      group.items = Array.from(itemMap.values());

      group.is_all_finished = group.items.every(
        (item) => item.is_finished === 1
      );
    });

    const groupedArray = Object.values(grouped).sort((a, b) => {
      return dayjs(b.register_date).diff(dayjs(a.register_date));
    });

    setGroupedData(groupedArray);
  };

  const handleFilterChange = (event, newValue) => {
    setFilterTab(newValue);
  };

  const getFilteredData = () => {
    if (filterTab === 0) {
      return groupedData;
    } else if (filterTab === 1) {
      return groupedData.filter((group) => !group.is_all_finished);
    } else {
      return groupedData.filter((group) => group.is_all_finished);
    }
  };

  const handleInputChange = (itemKey, value) => {
    setInputValues((prev) => ({
      ...prev,
      [itemKey]: value,
    }));
  };

  const handleFinishCheckboxChange = (itemKey, checked) => {
    setFinishCheckboxes((prev) => ({
      ...prev,
      [itemKey]: checked,
    }));
  };

  const handleStockIncreaseCheckboxChange = (itemKey, checked) => {
    setStockIncreaseCheckboxes((prev) => ({
      ...prev,
      [itemKey]: checked,
    }));
  };

  const handleSaveResult = async (item) => {
    const itemKey = `${item.instruction_id}_${item.item_id}_${item.machine_id}_${item.shift_id}`;
    const inputValue = inputValues[itemKey];
    const shouldFinish = finishCheckboxes[itemKey] || false;
    const shouldIncreaseStock = stockIncreaseCheckboxes[itemKey] || false;
  
    const displayQty =
      item.custom_qty !== null && item.custom_qty !== undefined
        ? item.custom_qty
        : item.ai_qty || 0;
  
    // ✅ 수정: 입력값 검증 로직
    // 1. null, undefined, 빈 문자열 체크
    if (inputValue === null || inputValue === undefined || inputValue === "") {
      setSnackbar({
        open: true,
        message: "실적 수량을 입력해주세요",
        severity: "warning",
      });
      return;
    }
  
    // 2. 숫자로 변환
    const resultQty = parseInt(inputValue, 10);
  
    // 3. 숫자 변환 실패 체크
    if (isNaN(resultQty)) {
      setSnackbar({
        open: true,
        message: "유효한 숫자를 입력해주세요",
        severity: "warning",
      });
      return;
    }
  
    // 4. 음수 체크
    if (resultQty < 0) {
      setSnackbar({
        open: true,
        message: "0 이상의 수량을 입력해주세요",
        severity: "warning",
      });
      return;
    }
  
    // ✅ 확인 메시지 생성
    let confirmMessage = "실적을 저장하시겠습니까?";
    if (shouldFinish && shouldIncreaseStock) {
      confirmMessage = "실적을 저장하고 마감 처리 + 재고 증가하시겠습니까?";
    } else if (shouldFinish) {
      confirmMessage = "실적을 저장하고 마감 처리하시겠습니까?";
    } else if (shouldIncreaseStock) {
      confirmMessage = "실적을 저장하고 재고를 증가시키겠습니까?";
    }
  
    if (!window.confirm(confirmMessage)) {
      return;
    }
  
    try {
      const saveData = {
        group_code,
        project_code,
        instruction_id: item.instruction_id,
        item_id: item.item_id,
        instruction_date: dayjs(item.instruction_date).format("YYYY-MM-DD"),
        result_qty: resultQty,
        is_finished: shouldFinish,
        increase_stock: shouldIncreaseStock,
      };
  
      await axios.post(
        `${TPC_URL}/api/instruction/saveInstructionResult`,
        saveData
      );
  
      let message = "실적이 저장되었습니다";
      if (shouldFinish && shouldIncreaseStock) {
        message += " (마감 완료 + 재고 증가)";
      } else if (shouldFinish) {
        message += " (마감 완료)";
      } else if (shouldIncreaseStock) {
        message += " (재고 증가)";
      }
  
      setSnackbar({
        open: true,
        message,
        severity: "success",
      });
  
      setInputValues((prev) => ({
        ...prev,
        [itemKey]: "",
      }));
  
      setFinishCheckboxes((prev) => ({
        ...prev,
        [itemKey]: false,
      }));
  
      setStockIncreaseCheckboxes((prev) => ({
        ...prev,
        [itemKey]: false,
      }));
  
      await fetchAllInstructions();
    } catch (error) {
      console.error("Error saving result:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || "실적 저장 실패",
        severity: "error",
      });
    }
  };
  
  // ✅ 전체 마감 Dialog 열기
  const handleCompleteAll = (group) => {
    const unfinishedItems = group.items.filter(
      (item) => item.is_finished !== 1
    );

    if (unfinishedItems.length === 0) {
      setSnackbar({
        open: true,
        message: "모든 항목이 이미 마감되었습니다",
        severity: "info",
      });
      return;
    }

    // ✅ 각 항목의 마감 수량 계산 (유저 입력값 우선)
    const itemsToComplete = unfinishedItems.map((item) => {
      const displayQty =
        item.custom_qty !== null && item.custom_qty !== undefined
          ? item.custom_qty
          : item.ai_qty || 0;

      const itemKey = `${item.instruction_id}_${item.item_id}_${item.machine_id}_${item.shift_id}`;
      
      // ✅ 유저가 입력한 값이 있으면 그 값 사용, 없으면 남은 수량 사용
      const userInputQty = inputValues[itemKey];
      const remainingQty = displayQty - item.result_qty;
      
      const finalQty = userInputQty !== undefined && userInputQty !== "" 
        ? parseInt(userInputQty) 
        : remainingQty;

      return {
        ...item,
        displayQty,
        remainingQty,
        finalQty, // ✅ 실제 마감될 수량
        itemKey,
      };
    });

    setConfirmDialog({
      open: true,
      group,
      items: itemsToComplete,
    });
  };

  // ✅ Dialog 닫기
  const handleCloseDialog = () => {
    setConfirmDialog({
      open: false,
      group: null,
      items: [],
    });
  };

  // ✅ Dialog에서 확인 버튼 클릭
  const handleConfirmCompleteAll = async () => {
    const { group, items } = confirmDialog;

    setLoading(true);
    handleCloseDialog();

    try {
      let successCount = 0;
      let failCount = 0;

      for (const item of items) {
        try {
          await axios.post(
            `${TPC_URL}/api/instruction/saveInstructionResult`,
            {
              group_code,
              project_code,
              instruction_id: item.instruction_id,
              item_id: item.item_id,
              instruction_date: dayjs(item.instruction_date).format(
                "YYYY-MM-DD"
              ),
              result_qty: item.finalQty, // ✅ 유저 입력값 또는 남은 수량
              is_finished: true,
              increase_stock: true,
            }
          );

          successCount++;
        } catch (error) {
          console.error(`Error processing ${item.item_code}:`, error);
          failCount++;
        }
      }

      let message = `전체 마감 완료 (성공: ${successCount}개`;
      if (failCount > 0) {
        message += `, 실패: ${failCount}개`;
      }
      message += ")";

      setSnackbar({
        open: true,
        message,
        severity: failCount > 0 ? "warning" : "success",
      });

      // ✅ 입력값 초기화
      items.forEach((item) => {
        setInputValues((prev) => ({
          ...prev,
          [item.itemKey]: "",
        }));
        setFinishCheckboxes((prev) => ({
          ...prev,
          [item.itemKey]: false,
        }));
        setStockIncreaseCheckboxes((prev) => ({
          ...prev,
          [item.itemKey]: false,
        }));
      });

      await fetchAllInstructions();
    } catch (error) {
      console.error("Error in handleConfirmCompleteAll:", error);
      setSnackbar({
        open: true,
        message: "전체 마감 처리 중 오류 발생",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getGroupMetadata = (group) => {
    const shifts = [...new Set(group.items.map((item) => item.shift_name))];
    const machines = [
      ...new Set(group.items.map((item) => item.machine_name)),
    ];
    return { shifts, machines };
  };

  const filteredData = getFilteredData();

  if (loading) {
    return (
      <Wrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress size={60} />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              작업지시서 실적 입력
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <Tabs value={filterTab} onChange={handleFilterChange}>
              <Tab label="전체" />
              <Tab label="발행됨" />
              <Tab label="마감됨" />
            </Tabs>
          </ToolbarRight>
        </ToolbarContainer>

        <ContentContainer>
          {filteredData.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "white",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                표시할 데이터가 없습니다.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredData.map((group) => {
                const { shifts, machines } = getGroupMetadata(group);
                const hasUnfinished = group.items.some(
                  (item) => item.is_finished !== 1
                );

                return (
                  <Grid item xs={12} md={6} key={group.instruction_id}>
                    <Card
                      sx={{
                        boxShadow: 3,
                        borderRadius: 2,
                        border: group.is_all_finished
                          ? "2px solid #4caf50"
                          : "2px solid transparent",
                        transition: "all 0.3s",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        opacity: group.is_all_finished ? 0.85 : 1,
                        "&:hover": {
                          boxShadow: 6,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 2.5,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1.5,
                            pb: 1.5,
                            borderBottom: "2px solid #e0e0e0",
                            gap: 2,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight={600}>
                              {group.instruction_code}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              등록:{" "}
                              {dayjs(group.register_date).format(
                                "MM/DD HH:mm"
                              )}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              icon={
                                <CalendarTodayIcon sx={{ fontSize: 14 }} />
                              }
                              label={dayjs(group.instruction_date).format(
                                "MM/DD"
                              )}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                            {shifts.map((shift, idx) => (
                              <Chip
                                key={idx}
                                icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                label={shift}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {machines.map((machine, idx) => (
                              <Chip
                                key={idx}
                                icon={<FactoryIcon sx={{ fontSize: 14 }} />}
                                label={machine}
                                size="small"
                                variant="outlined"
                              />
                            ))}

                            {hasUnfinished && (
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleCompleteAll(group)}
                                sx={{
                                  fontWeight: "bold",
                                  boxShadow: 2,
                                }}
                              >
                                전체마감
                              </Button>
                            )}

                            {group.is_all_finished && (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="마감 완료"
                                color="success"
                                size="small"
                                sx={{ fontWeight: "bold" }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Stack spacing={1.5} sx={{ flex: 1 }}>
                          {group.items.map((item, index) => {
                            const displayQty =
                              item.custom_qty !== null &&
                              item.custom_qty !== undefined
                                ? item.custom_qty
                                : item.ai_qty || 0;

                            const isFinished = item.is_finished === 1;
                            const itemKey = `${item.instruction_id}_${item.item_id}_${item.machine_id}_${item.shift_id}`;

                            const remainingQty = displayQty - item.result_qty;
                            return (
                              <Paper
                                key={itemKey}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  bgcolor: isFinished ? "#f5f5f5" : "white",
                                  border: isFinished
                                    ? "2px solid #4caf50"
                                    : "1px solid #e0e0e0",
                                  borderRadius: 1,
                                  position: "relative",
                                  opacity: isFinished ? 0.7 : 1,
                                }}
                              >
                                {isFinished && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: -8,
                                      right: 10,
                                      bgcolor: "#4caf50",
                                      color: "white",
                                      px: 1.5,
                                      py: 0.3,
                                      borderRadius: 1,
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      boxShadow: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                    마감 완료
                                  </Box>
                                )}

                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    mb: 1,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight="bold"
                                      sx={{
                                        color: isFinished
                                          ? "#757575"
                                          : "inherit",
                                      }}
                                    >
                                      {item.item_code}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {item.material_grouping}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Chip
                                      icon={
                                        <AccessTimeIcon
                                          sx={{ fontSize: 12 }}
                                        />
                                      }
                                      label={item.shift_name}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Box sx={{ display: "flex", gap: 2 }}>
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        지시수량
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{
                                          color: isFinished
                                            ? "#757575"
                                            : "inherit",
                                        }}
                                      >
                                        {displayQty.toLocaleString()}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        완료수량
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color={
                                          isFinished
                                            ? "success.main"
                                            : "primary"
                                        }
                                      >
                                        {item.result_qty.toLocaleString()}
                                      </Typography>
                                    </Box>
                                    {!isFinished && (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          남은수량
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                          color={
                                            remainingQty > 0
                                              ? "error.main"
                                              : "success.main"
                                          }
                                        >
                                          {remainingQty.toLocaleString()}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>

                                  {isFinished ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        bgcolor: "#e0e0e0",
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <CheckCircleIcon
                                        sx={{ fontSize: 18, color: "#4caf50" }}
                                      />
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color="#757575"
                                      >
                                        실적 입력 완료
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <TextField
                                        placeholder="실적"
                                        type="number"
                                        size="small"
                                        value={
                                          inputValues[itemKey] !== undefined &&
                                          inputValues[itemKey] !== ""
                                            ? inputValues[itemKey]
                                            : remainingQty
                                        }
                                        onChange={(e) =>
                                          handleInputChange(
                                            itemKey,
                                            e.target.value
                                          )
                                        }
                                        sx={{ width: 100 }}
                                        InputProps={{
                                          inputProps: { min: 0 },
                                        }}
                                        helperText={`남음: ${remainingQty}`}
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              finishCheckboxes[itemKey] ||
                                              false
                                            }
                                            onChange={(e) =>
                                              handleFinishCheckboxChange(
                                                itemKey,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        }
                                        label={
                                          <Typography variant="caption">
                                            마감
                                          </Typography>
                                        }
                                        sx={{ m: 0 }}
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              stockIncreaseCheckboxes[
                                                itemKey
                                              ] || false
                                            }
                                            onChange={(e) =>
                                              handleStockIncreaseCheckboxChange(
                                                itemKey,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        }
                                        label={
                                          <Typography variant="caption">
                                            재고증가
                                          </Typography>
                                        }
                                        sx={{ m: 0 }}
                                      />
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleSaveResult(item)}
                                      >
                                        <SaveIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </ContentContainer>
      </FrameContainer>

      {/* ✅ 전체 마감 확인 Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          전체 마감 확인
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              작업지시서: {confirmDialog.group?.instruction_code}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              다음 항목들을 마감하고 재고를 증가시킵니다.
            </Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell>
                    <strong>품목코드</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>지시수량</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>완료수량</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>남은수량</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>마감수량</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {confirmDialog.items.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": { bgcolor: "#f9f9f9" },
                      // ✅ 지시수량보다 많은 경우 배경색 변경
                      bgcolor:
                        item.result_qty + item.finalQty > item.displayQty
                          ? "#fff3e0"
                          : "inherit",
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.item_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.material_grouping}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.displayQty.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      {item.result_qty.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={
                          item.remainingQty > 0 ? "error.main" : "success.main"
                        }
                        fontWeight="bold"
                      >
                        {item.remainingQty.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                        >
                          {item.finalQty.toLocaleString()}
                        </Typography>
                        {/* ✅ 지시수량 초과 경고 */}
                        {item.result_qty + item.finalQty > item.displayQty && (
                          <Chip
                            label="초과"
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ✅ 경고 메시지 */}
          {confirmDialog.items.some(
            (item) => item.result_qty + item.finalQty > item.displayQty
          ) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ 일부 항목이 지시수량을 초과합니다.
              </Typography>
              <Typography variant="caption">
                초과된 수량도 그대로 저장됩니다.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            취소
          </Button>
          <Button
            onClick={handleConfirmCompleteAll}
            variant="contained"
            color="error"
            startIcon={<CheckCircleIcon />}
          >
            전체 마감 및 재고 증가
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default WorkOrderLog;
